import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Household,
  HouseholdLink,
  HouseholdLinkKind,
  PersonSummary,
  PersonStatus,
  useAddHouseholdLink,
  useDeleteHouseholdLink,
  useDeleteInteraction,
  useHousehold,
  usePatchHousehold,
  usePatchPerson,
  usePerson,
  useSearch,
  useSectorCatalog,
  useUpdateHouseholdLink,
} from '../api';
import ChangeLog from '../components/ChangeLog';
import LogInteractionForm from '../components/LogInteractionForm';
import InteractionEditForm from '../components/InteractionEditForm';
import { HouseGlyph } from '../components/Glyphs';
import { HouseholdMinisteringPanel } from '../components/Ministering';
import { TagsCard } from '../components/Tags';
import { WardMapCard } from '../components/WardMap';
import { printPage } from '../print';
import { formatInteractionType } from '../interactionTypes';

const LINK_KINDS: { kind: HouseholdLinkKind; label: string }[] = [
  { kind: 'relatives', label: 'Relatives' },
  { kind: 'same_address', label: 'Same address' },
  { kind: 'neighbors', label: 'Neighbors' },
  { kind: 'friends', label: 'Friends' },
];
const LINK_LABEL: Record<HouseholdLinkKind, string> = LINK_KINDS.reduce(
  (acc, { kind, label }) => ({ ...acc, [kind]: label }),
  {} as Record<HouseholdLinkKind, string>,
);

type HouseholdEdits = Partial<Pick<Household, 'name_directory' | 'formatted_address' | 'notes_override'>>;

const STATUS_LABEL: Record<PersonStatus, string> = {
  active: 'Active',
  semi_active: 'Semi-Active',
  inactive: 'Inactive',
};

const STATUS_BADGE: Record<PersonStatus | 'Mixed', string> = {
  active:      'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300',
  semi_active: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300',
  inactive:    'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
  Mixed:       'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400',
};

function householdStatus(members: PersonSummary[]): { label: string; key: PersonStatus | 'Mixed' } | null {
  const statuses = members.map((m) => m.status_override).filter(Boolean) as PersonStatus[];
  if (statuses.length === 0) return null;
  const unique = new Set(statuses);
  if (unique.size === 1) return { label: STATUS_LABEL[statuses[0]], key: statuses[0] };
  return { label: 'Mixed', key: 'Mixed' };
}

export default function HouseholdDetail() {
  const { id = '' } = useParams();
  const { data, isLoading, error } = useHousehold(id);

  if (isLoading) return <p className="text-slate-500 dark:text-slate-400">Loading…</p>;
  if (error) return <p className="text-red-600 dark:text-red-400">Error loading household.</p>;
  if (!data) return null;

  const status = householdStatus(data.members);

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-[2fr_3fr] print:grid-cols-1">
      <section className="space-y-4">
        <header>
          <div className="flex items-start justify-between">
            <h1 className="flex items-center gap-2 text-lg font-semibold">
              <HouseGlyph />
              {data.name_directory ?? '(unnamed)'}
            </h1>
            <span className="flex items-center gap-2">
              {status && (
                <span className={`rounded px-2 py-0.5 text-xs ${STATUS_BADGE[status.key]}`}>{status.label}</span>
              )}
              <button
                onClick={printPage}
                className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                title="Print or save as PDF"
              >
                Print
              </button>
            </span>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            <MapsLink address={data.formatted_address} />
          </p>
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
            {data.source === 'lcr'
              ? `LCR-synced ${data.lcr_synced_at ? new Date(data.lcr_synced_at).toLocaleDateString() : ''}`
              : 'Steward-only household'}
          </p>
        </header>

        <WardMapCard lat={data.lat} lng={data.lng} inBoundary={data.sector_macro != null} />

        <HouseholdInfoCard household={data} />

        <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="border-b border-slate-200 dark:border-slate-700 px-3 py-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Members
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.members.map((m) => (
              <li key={m.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <Link className="hover:underline" to={`/people/${m.id}`}>
                  {m.name_full}
                </Link>
                <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  {m.household_role}{m.age != null ? ` · ${m.age}y` : ''}
                  {m.status_override && (
                    <span className={`rounded px-1.5 py-0.5 ${STATUS_BADGE[m.status_override]}`}>
                      {STATUS_LABEL[m.status_override]}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <RelatedHouseholdsCard household={data} />

        <HouseholdMinisteringPanel householdId={data.id} />

        <SectorCard household={data} />

        <HouseholdTagsCard household={data} />
      </section>

      <section className="space-y-4">
        <NotesCard household={data} />
        {data.members.length === 1 && (
          <LoneMemberNotesCard personId={data.members[0].id} />
        )}
        <InteractionsSection householdId={data.id} interactions={data.interactions} />
        <div className="print:hidden">
          <h2 className="mb-2 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Change history</h2>
          <ChangeLog resourceType="household" resourceId={data.id} limit={50} />
        </div>
      </section>
    </div>
  );
}

function InteractionsSection({
  householdId,
  interactions,
}: {
  householdId: string;
  interactions: Household['interactions'];
}) {
  const [logging, setLogging] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const deleteInteraction = useDeleteInteraction();

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Interactions</h2>
        {!logging && (
          <button
            onClick={() => setLogging(true)}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            + Log interaction
          </button>
        )}
      </div>
      {logging && (
        <div className="mb-3">
          <LogInteractionForm
            householdIds={[householdId]}
            onDone={() => setLogging(false)}
            onCancel={() => setLogging(false)}
          />
        </div>
      )}
      {interactions.length === 0 && !logging ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">No interactions logged for this household yet.</p>
      ) : interactions.length > 0 ? (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          {interactions.map((i) => (
            <li key={i.id} className="px-3 py-2 text-sm">
              {editingId === i.id ? (
                <InteractionEditForm
                  interaction={i}
                  onDone={() => setEditingId(null)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
              <>
              <div className="flex justify-between">
                <span className="flex items-center gap-2 font-medium">
                  {formatInteractionType(i.type)}
                  {i.performed_by && (
                    <span className="text-xs font-normal text-slate-500 dark:text-slate-400">by {i.performed_by}</span>
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {new Date(i.occurred_at).toLocaleString()}
                  </span>
                  <button
                    onClick={() => setEditingId(i.id)}
                    className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => { if (confirm('Delete this interaction?')) deleteInteraction.mutate(i.id); }}
                    className="text-xs text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400"
                  >
                    Delete
                  </button>
                </div>
              </div>
              {i.notes && (
                <div className="prose dark:prose-invert prose-sm mt-1 max-w-none text-slate-700 dark:text-slate-300">
                  <ReactMarkdown>{i.notes}</ReactMarkdown>
                </div>
              )}
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{i.created_by}</p>
              </>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function HouseholdTagsCard({ household: h }: { household: Household }) {
  const patch = usePatchHousehold();
  return (
    <TagsCard
      tags={h.tags ?? []}
      saving={patch.isPending}
      error={patch.error as Error | null}
      onSave={(next) => patch.mutate({ id: h.id, body: { tags: next } })}
    />
  );
}

function HouseholdInfoCard({ household: h }: { household: Household }) {
  const patch = usePatchHousehold();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<HouseholdEdits>({});

  const start = () => {
    setDraft({ name_directory: h.name_directory, formatted_address: h.formatted_address });
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setDraft({});
    patch.reset();
  };
  const save = () => {
    const body: HouseholdEdits = {};
    if (draft.name_directory !== h.name_directory) body.name_directory = draft.name_directory;
    if (draft.formatted_address !== h.formatted_address) body.formatted_address = draft.formatted_address;
    if (Object.keys(body).length === 0) { cancel(); return; }
    patch.mutate({ id: h.id, body }, { onSuccess: () => { setEditing(false); setDraft({}); } });
  };

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <CardHeader title="Household info" editing={editing} onEdit={start} onSave={save} onCancel={cancel} saving={patch.isPending} />
      <dl>
        <Row label="Directory name">
          {editing ? (
            <Input value={draft.name_directory ?? ''} onChange={(v) => setDraft({ ...draft, name_directory: v || null })} placeholder='e.g. "Smith, John & Jane"' />
          ) : display(h.name_directory)}
        </Row>
        <Row label="Address">
          {editing ? (
            <Input value={draft.formatted_address ?? ''} onChange={(v) => setDraft({ ...draft, formatted_address: v || null })} placeholder="123 Main St, City, ST 12345" />
          ) : (
            <MapsLink address={h.formatted_address} />
          )}
        </Row>
      </dl>
      {patch.error && (
        <p className="border-t border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-3 py-1 text-xs text-rose-700 dark:text-rose-300">
          Save failed: {(patch.error as Error).message}
        </p>
      )}
    </div>
  );
}

function NotesCard({ household: h }: { household: Household }) {
  const patch = usePatchHousehold();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const start = () => { setDraft(h.notes_override ?? ''); setEditing(true); };
  const cancel = () => { setEditing(false); patch.reset(); };
  const save = () => {
    const next = draft.trim() ? draft : null;
    if (next === (h.notes_override ?? null)) { cancel(); return; }
    patch.mutate({ id: h.id, body: { notes_override: next } }, { onSuccess: () => setEditing(false) });
  };

  if (!editing && !h.notes_override) {
    return (
      <div className="rounded border border-dashed border-slate-200 dark:border-slate-700 px-3 py-2 text-sm">
        <button className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300" onClick={start}>+ Add notes</button>
      </div>
    );
  }

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <CardHeader title="Notes" editing={editing} onEdit={start} onSave={save} onCancel={cancel} saving={patch.isPending} />
      <div className="px-3 py-2">
        {editing ? (
          <textarea className="w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm" rows={5} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Household-level notes — context that doesn't fit on a person." />
        ) : (
          <div className="prose dark:prose-invert prose-sm max-w-none">
            <ReactMarkdown>{h.notes_override ?? ''}</ReactMarkdown>
          </div>
        )}
      </div>
      {patch.error && (
        <p className="border-t border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-3 py-1 text-xs text-rose-700 dark:text-rose-300">
          Save failed: {(patch.error as Error).message}
        </p>
      )}
    </div>
  );
}

function LoneMemberNotesCard({ personId }: { personId: string }) {
  const { data: person, isLoading } = usePerson(personId);
  const patch = usePatchPerson();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (isLoading) return null;
  if (!person) return null;

  const start = () => { setDraft(person.notes_override ?? ''); setEditing(true); };
  const cancel = () => { setEditing(false); patch.reset(); };
  const save = () => {
    const next = draft.trim() ? draft : null;
    if (next === (person.notes_override ?? null)) { cancel(); return; }
    patch.mutate(
      { id: person.id, body: { notes_override: next } },
      { onSuccess: () => setEditing(false) },
    );
  };

  if (!editing && !person.notes_override) {
    return (
      <div className="rounded border border-dashed border-slate-200 dark:border-slate-700 px-3 py-2 text-sm">
        <button className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300" onClick={start}>
          + Add notes about {person.name_full ?? 'this person'}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <CardHeader
        title={`Notes — ${person.name_full ?? 'person'}`}
        editing={editing}
        onEdit={start}
        onSave={save}
        onCancel={cancel}
        saving={patch.isPending}
      />
      <div className="px-3 py-2">
        {editing ? (
          <textarea
            className="w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
            rows={5}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Anything worth remembering about this person."
          />
        ) : (
          <div className="prose dark:prose-invert prose-sm max-w-none">
            <ReactMarkdown>{person.notes_override ?? ''}</ReactMarkdown>
          </div>
        )}
      </div>
      {patch.error && (
        <p className="border-t border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-3 py-1 text-xs text-rose-700 dark:text-rose-300">
          Save failed: {(patch.error as Error).message}
        </p>
      )}
    </div>
  );
}

/** Which sector a household belongs to, and the override for when the rules
 *  get it wrong.
 *
 *  Two layers, because they answer different questions: the macro sector is
 *  the neighborhood a leader takes, the micro sector is one walk inside it.
 *  They override independently — moving a street to the next leader should
 *  not disturb the walks within it.
 *
 *  The pickers offer the defined sectors rather than free text. A hand-typed
 *  name that is almost right creates a sector of one household that nobody
 *  notices until it is on a clipboard during an incident.
 */
function SectorCard({ household: h }: { household: Household }) {
  const patch = usePatchHousehold();
  const { data: catalog } = useSectorCatalog();
  const [editing, setEditing] = useState(false);
  const [macro, setMacro] = useState<string | null>(null);
  const [micro, setMicro] = useState<string | null>(null);

  const start = () => {
    setMacro(h.sector_macro_override);
    setMicro(h.sector_micro_override);
    setEditing(true);
  };
  const cancel = () => { setEditing(false); patch.reset(); };
  const save = () => {
    const body: Record<string, string | null> = {};
    if (macro !== h.sector_macro_override) body.sector_macro = macro;
    if (micro !== h.sector_micro_override) body.sector_micro = micro;
    if (Object.keys(body).length === 0) { cancel(); return; }
    patch.mutate({ id: h.id, body }, { onSuccess: () => setEditing(false) });
  };

  const macros = catalog?.macros ?? [];
  const allMicros = macros.flatMap((m) => m.micros.map((mi) => ({ macro: m.name, micro: mi })));

  const value = (resolved: string | null, auto: string | null, override: string | null) => (
    <>
      {display(resolved)}
      {override && (
        <span className="ml-2 rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[11px] text-amber-800 dark:text-amber-300">
          overridden
        </span>
      )}
      {override && auto && auto !== override && (
        <div className="text-xs text-slate-400 dark:text-slate-500">Rules say: {auto}</div>
      )}
    </>
  );

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <CardHeader title="Sector" editing={editing} onEdit={start} onSave={save} onCancel={cancel} saving={patch.isPending} />
      <dl>
        <Row label="Ward sector">
          {editing ? (
            <select
              className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-sm"
              value={macro ?? ''}
              onChange={(e) => setMacro(e.target.value || null)}
            >
              <option value="">Automatic{h.sector_macro_auto ? ` — ${h.sector_macro_auto}` : ''}</option>
              {macros.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
            </select>
          ) : value(h.sector_macro, h.sector_macro_auto, h.sector_macro_override)}
        </Row>
        <Row label="Walk">
          {editing ? (
            <select
              className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-sm"
              value={micro ?? ''}
              onChange={(e) => setMicro(e.target.value || null)}
            >
              <option value="">Automatic{h.sector_micro_auto ? ` — ${h.sector_micro_auto}` : ''}</option>
              {macros.map((m) => (
                <optgroup key={m.name} label={m.name}>
                  {m.micros.map((mi) => <option key={mi} value={mi}>{mi}</option>)}
                </optgroup>
              ))}
            </select>
          ) : value(h.sector_micro, h.sector_micro_auto, h.sector_micro_override)}
        </Row>
        {!editing && (
          <Row label="Matched by">
            {h.sector_unit
              ? <span className="text-xs text-slate-500 dark:text-slate-400">{h.sector_unit}</span>
              : <span className="text-xs text-slate-400 dark:text-slate-500">
                  No rule matched this address — assign a sector by hand.
                </span>}
          </Row>
        )}
      </dl>
      {editing && allMicros.length === 0 && (
        <p className="border-t border-slate-200 dark:border-slate-700 px-3 py-1 text-xs text-slate-500 dark:text-slate-400">
          Sector list unavailable.
        </p>
      )}
      {patch.error && (
        <p className="border-t border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-3 py-1 text-xs text-rose-700 dark:text-rose-300">
          Save failed: {(patch.error as Error).message}
        </p>
      )}
    </div>
  );
}

function CardHeader({ title, editing, saving, onEdit, onSave, onCancel }: {
  title: string; editing: boolean; saving: boolean;
  onEdit: () => void; onSave: () => void; onCancel: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-3 py-1">
      <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</span>
      {editing ? (
        <div className="flex gap-1">
          <button className="rounded bg-slate-700 dark:bg-slate-600 px-2 py-0.5 text-xs text-white disabled:opacity-50" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400" onClick={onCancel} disabled={saving}>Cancel</button>
        </div>
      ) : (
        <button className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300" onClick={onEdit}>Edit</button>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 border-b border-slate-100 dark:border-slate-800 px-3 py-1 text-sm last:border-b-0">
      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="col-span-2 text-slate-700 dark:text-slate-300">{children}</dd>
    </div>
  );
}

function Input({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input className="w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-sm" type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  );
}

function display(v: string | null | undefined): React.ReactNode {
  return v ? v : <span className="text-slate-400 dark:text-slate-500">—</span>;
}

function stripHtml(s: string | null | undefined): string | null {
  if (!s) return null;
  return s.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '').trim() || null;
}

function MapsLink({ address }: { address: string | null | undefined }) {
  const lines = splitHtmlLines(address);
  if (lines.length === 0) return <span className="text-slate-400 dark:text-slate-500">—</span>;
  const flat = lines.join(' ');
  const href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(flat)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:text-slate-900 dark:hover:text-slate-100 hover:underline"
    >
      {lines.map((line, i) => (
        <span key={i} className="block">{line}</span>
      ))}
    </a>
  );
}

function splitHtmlLines(s: string | null | undefined): string[] {
  if (!s) return [];
  return s
    .split(/<br\s*\/?>/i)
    .map((part) => part.replace(/<[^>]+>/g, '').trim())
    .filter(Boolean);
}

function RelatedHouseholdsCard({ household: h }: { household: Household }) {
  const [adding, setAdding] = useState(false);
  const links = h.links ?? [];

  // Group links by the other household so each related household renders as
  // a single row carrying all of its relationship kinds.
  const groups = groupLinksByOther(links);

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-3 py-1">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Related households</span>
        {!adding && (
          <button
            className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            onClick={() => setAdding(true)}
          >
            + Add
          </button>
        )}
      </div>
      {adding && (
        <AddLinkRow
          household={h}
          onDone={() => setAdding(false)}
          onCancel={() => setAdding(false)}
        />
      )}
      {groups.length === 0 && !adding ? (
        <p className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">No related households yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {groups.map((g) => (
            <RelatedHouseholdRow key={g.other_id} household={h} group={g} />
          ))}
        </ul>
      )}
    </div>
  );
}

interface LinkGroup {
  other_id: string;
  name_directory: string | null;
  formatted_address: string | null;
  links: HouseholdLink[];
}

function groupLinksByOther(links: HouseholdLink[]): LinkGroup[] {
  const byId = new Map<string, LinkGroup>();
  for (const l of links) {
    const existing = byId.get(l.other_id);
    if (existing) {
      existing.links.push(l);
    } else {
      byId.set(l.other_id, {
        other_id: l.other_id,
        name_directory: l.name_directory,
        formatted_address: l.formatted_address,
        links: [l],
      });
    }
  }
  // Sort each group's links by the canonical kind order; sort groups by name.
  const kindOrder = new Map(LINK_KINDS.map((k, i) => [k.kind, i]));
  for (const g of byId.values()) {
    g.links.sort(
      (a, b) => (kindOrder.get(a.kind) ?? 99) - (kindOrder.get(b.kind) ?? 99),
    );
  }
  return [...byId.values()].sort((a, b) =>
    (a.name_directory ?? '').localeCompare(b.name_directory ?? ''),
  );
}

function RelatedHouseholdRow({
  household: h,
  group,
}: {
  household: Household;
  group: LinkGroup;
}) {
  return (
    <li className="px-3 py-2 text-sm">
      <div className="flex items-baseline justify-between gap-2">
        <Link
          to={`/households/${group.other_id}`}
          className="min-w-0 truncate font-medium text-slate-800 dark:text-slate-200 hover:underline"
        >
          {group.name_directory ?? '(unnamed household)'}
        </Link>
        <div className="flex shrink-0 flex-wrap items-center gap-1">
          {group.links.map((l) => (
            <span
              key={l.kind}
              className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px] text-slate-600 dark:text-slate-400"
              title={LINK_LABEL[l.kind]}
            >
              {LINK_LABEL[l.kind]}
            </span>
          ))}
        </div>
      </div>
      {group.formatted_address && (
        <div className="truncate text-xs text-slate-500 dark:text-slate-400">{stripHtml(group.formatted_address)}</div>
      )}
      <ul className="mt-1 space-y-1">
        {group.links.map((link) => (
          <LinkKindLine key={link.kind} household={h} link={link} />
        ))}
      </ul>
    </li>
  );
}

function LinkKindLine({ household: h, link }: { household: Household; link: HouseholdLink }) {
  const update = useUpdateHouseholdLink();
  const del = useDeleteHouseholdLink();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(link.note ?? '');

  const startEdit = () => { setDraft(link.note ?? ''); setEditing(true); };
  const saveNote = () => {
    const next = draft.trim() ? draft.trim() : null;
    if (next === (link.note ?? null)) {
      setEditing(false);
      return;
    }
    update.mutate(
      { id: h.id, other_id: link.other_id, kind: link.kind, note: next },
      { onSuccess: () => setEditing(false) },
    );
  };
  const remove = () => {
    if (!confirm(`Remove ${LINK_LABEL[link.kind].toLowerCase()} link with ${link.name_directory ?? 'this household'}?`)) return;
    del.mutate({ id: h.id, other_id: link.other_id, kind: link.kind });
  };

  return (
    <li className="flex flex-col gap-1 rounded px-1 py-0.5 text-xs">
      <div className="flex items-baseline justify-between gap-2">
        <span className="flex-1 text-slate-600 dark:text-slate-400">
          <span className="font-medium text-slate-500 dark:text-slate-400">{LINK_LABEL[link.kind]}</span>
          {link.note && <span className="ml-1.5 text-slate-600 dark:text-slate-400">{link.note}</span>}
          {!link.note && !editing && <span className="ml-1.5 text-slate-400 dark:text-slate-500">—</span>}
        </span>
        <div className="flex shrink-0 gap-2 text-xs text-slate-400 dark:text-slate-500">
          {!editing && (
            <button className="hover:text-slate-700 dark:hover:text-slate-300" onClick={startEdit}>
              {link.note ? 'Edit note' : '+ Note'}
            </button>
          )}
          <button className="hover:text-red-600 dark:hover:text-red-400" onClick={remove} disabled={del.isPending}>
            Remove
          </button>
        </div>
      </div>
      {editing && (
        <div className="flex items-center gap-1">
          <input
            className="flex-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
            type="text"
            value={draft}
            placeholder='e.g. "sisters", "lives in basement apt"'
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
          />
          <button
            className="rounded bg-slate-700 dark:bg-slate-600 px-2 py-0.5 text-xs text-white disabled:opacity-50"
            onClick={saveNote}
            disabled={update.isPending}
          >
            Save
          </button>
          <button
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400"
            onClick={() => setEditing(false)}
            disabled={update.isPending}
          >
            Cancel
          </button>
        </div>
      )}
      {(update.error || del.error) && (
        <p className="text-xs text-rose-700 dark:text-rose-300">
          {(update.error as Error | null)?.message || (del.error as Error | null)?.message}
        </p>
      )}
    </li>
  );
}

function AddLinkRow({
  household: h,
  onDone,
  onCancel,
}: {
  household: Household;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const [kind, setKind] = useState<HouseholdLinkKind>('relatives');
  const [note, setNote] = useState('');
  const add = useAddHouseholdLink();

  const submit = () => {
    if (!picked) return;
    add.mutate(
      { id: h.id, other_id: picked.id, kind, note: note.trim() || null },
      { onSuccess: () => onDone() },
    );
  };

  const existingKey = new Set((h.links ?? []).map((l) => `${l.kind}:${l.other_id}`));

  return (
    <div className="space-y-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-sm">
      {!picked ? (
        <HouseholdPicker
          excludeId={h.id}
          excludeKeys={existingKey}
          kind={kind}
          query={query}
          onQueryChange={setQuery}
          onPick={(p) => setPicked(p)}
        />
      ) : (
        <div className="flex items-center justify-between rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1">
          <span className="truncate">{picked.name}</span>
          <button className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300" onClick={() => setPicked(null)}>
            Change
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
          value={kind}
          onChange={(e) => setKind(e.target.value as HouseholdLinkKind)}
        >
          {LINK_KINDS.map((l) => (
            <option key={l.kind} value={l.kind}>{l.label}</option>
          ))}
        </select>
        <input
          className="min-w-[10rem] flex-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
          type="text"
          placeholder='Optional note — e.g. "sisters"'
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <div className="flex gap-1">
          <button
            className="rounded bg-slate-700 dark:bg-slate-600 px-2 py-0.5 text-xs text-white disabled:opacity-50"
            onClick={submit}
            disabled={!picked || add.isPending || existingKey.has(`${kind}:${picked.id}`)}
          >
            {add.isPending ? 'Adding…' : 'Add'}
          </button>
          <button
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400"
            onClick={onCancel}
            disabled={add.isPending}
          >
            Cancel
          </button>
        </div>
      </div>
      {picked && existingKey.has(`${kind}:${picked.id}`) && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          A {LINK_LABEL[kind].toLowerCase()} link with this household already exists.
        </p>
      )}
      {add.error && (
        <p className="text-xs text-rose-700 dark:text-rose-300">Add failed: {(add.error as Error).message}</p>
      )}
    </div>
  );
}

function HouseholdPicker({
  excludeId,
  excludeKeys,
  kind,
  query,
  onQueryChange,
  onPick,
}: {
  excludeId: string;
  excludeKeys: Set<string>;
  kind: HouseholdLinkKind;
  query: string;
  onQueryChange: (q: string) => void;
  onPick: (p: { id: string; name: string }) => void;
}) {
  const trimmed = query.trim();
  const params = trimmed.length >= 2 ? { q: trimmed } : ({} as Record<string, string>);
  const { data, isFetching } = useSearch('households', params);

  const results = (data?.results ?? []).filter((r: any) => r.id !== excludeId);

  return (
    <div className="space-y-1">
      <input
        className="w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-sm"
        type="text"
        placeholder="Search households by name…"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        autoFocus
      />
      {trimmed.length < 2 ? (
        <p className="text-xs text-slate-400 dark:text-slate-500">Type at least 2 characters to search.</p>
      ) : (
        <ul className="max-h-48 overflow-y-auto rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          {isFetching && <li className="px-2 py-1 text-xs text-slate-500 dark:text-slate-400">Searching…</li>}
          {!isFetching && results.length === 0 && (
            <li className="px-2 py-1 text-xs text-slate-500 dark:text-slate-400">No matches.</li>
          )}
          {results.map((r: any) => {
            const alreadyLinked = excludeKeys.has(`${kind}:${r.id}`);
            return (
              <li key={r.id}>
                <button
                  type="button"
                  className="block w-full px-2 py-1 text-left text-xs hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
                  onClick={() => onPick({ id: r.id, name: r.name_directory ?? '(unnamed)' })}
                  disabled={alreadyLinked}
                  title={alreadyLinked ? `already linked as ${LINK_LABEL[kind].toLowerCase()}` : undefined}
                >
                  <div className="font-medium text-slate-800 dark:text-slate-200">{r.name_directory ?? '(unnamed)'}</div>
                  {r.formatted_address && (
                    <div className="truncate text-slate-500 dark:text-slate-400">{stripHtml(r.formatted_address)}</div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
